import { Fragment, type ReactNode } from 'react';

import { type LegalDoc } from './legalContent';
import styles from './LegalDocModal.module.css';

/**
 * Body of a legal document from docs/legal/ (PLA-1091), rendered from the
 * parsed blocks. Shared by LegalDocModal (the sign-in footer viewer) and the
 * AI Governance acceptance at sign-in (PLA-1110), which shows the policy
 * inline so it can be read and accepted in one place.
 */

/** Inline markdown subset: **bold**, *emphasis*, auto-linked emails, and URLs. */
const INLINE_TOKEN = /(\*\*[^*]+\*\*|\*[^*]+\*|https?:\/\/[^\s)]*[^\s).,;:!?]|[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+)/g;

function renderInline(text: string): ReactNode {
  return text
    .split(INLINE_TOKEN)
    .filter(Boolean)
    .map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return <em key={i}>{part.slice(1, -1)}</em>;
      }
      if (/^https?:\/\//.test(part)) {
        return (
          <a key={i} className={styles.mail} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </a>
        );
      }
      if (/^[A-Za-z0-9._%+-]+@/.test(part)) {
        return (
          <a key={i} className={styles.mail} href={`mailto:${part}`}>
            {part}
          </a>
        );
      }
      return <Fragment key={i}>{part}</Fragment>;
    });
}

export function LegalDocArticle({ doc }: { doc: LegalDoc }) {
  return (
    <article className={styles.doc}>
      {doc.blocks.map((block, i) => {
        switch (block.kind) {
          case 'heading':
            return (
              <h3 key={i} className={styles.heading}>
                {renderInline(block.text)}
              </h3>
            );
          case 'note':
            return (
              <p key={i} className={styles.note}>
                {renderInline(block.text)}
              </p>
            );
          case 'list':
            return (
              <ul key={i} className={styles.list}>
                {block.items.map((item, j) => (
                  <li key={j}>{renderInline(item)}</li>
                ))}
              </ul>
            );
          default:
            return (
              <p key={i} className={styles.paragraph}>
                {renderInline(block.text)}
              </p>
            );
        }
      })}
    </article>
  );
}
